import { FC, ReactNode } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

interface Props {
  icon: ReactNode;
  iconColor: string;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  /** Optional border accent (e.g. error tint for the failed card). */
  accentBorder?: string;
}

/** A single dashboard stat tile: icon chip, label, big value and a caption. */
export const StatCard: FC<Props> = observer(({ icon, iconColor, label, value, sub, accentBorder }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${accentBorder ?? theme.palette.grey[300]}`,
        borderRadius: 3.5,
        px: 2.125,
        py: 1.875,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 2,
            backgroundColor: theme.palette.grey[200],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: iconColor,
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary, fontWeight: 500 }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 32, fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1 }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: 11.5, color: theme.palette.text.tertiary, mt: 0.875 }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
});
